import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { Icon } from "@mui/material";
import { WoningIcon } from "../images/icons/svgIcons";
import * as functions from "./opbouwFunctions";
import { getTranslations } from "../Shared/translations";
import {
  dbItem,
  kamerItem,
  modsArray2,
  modsForOfferteArray,
} from "./../Shared/sharedInterfaces";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import BuildingTree, { buildingTreeRefs } from "./Woning/buildingtree";
import OutputModulesTree from "./Modules/outputmodules";
import Overzicht from "./Overzicht/overzicht";
import "./opbouw.css";
import AddKast from "./../Screens/addKast";
import AddCtd from "../Screens/addCtd";
import * as myTreeIcons from "./../images/icons/treeicons";

interface MyProps {
  loadedWoning: any;
  uitvoering: string;
  onDbChange: (db: dbItem[]) => void;
  onOfferteItemsUpdated: (
    t: modsForOfferteArray[],
    sa: modsForOfferteArray[],
  ) => void;
  onInDienstChanged: (t: any) => void;
  pakketten: boolean;
  rel04_08: boolean;
  indienst: boolean;
}

export interface opbouwRefs {
  childFunction: (loadedWoning: any, foced: boolean) => void;
}

const Opbouw = forwardRef<opbouwRefs, MyProps>(
  (
    {
      loadedWoning,
      uitvoering,
      onDbChange,
      onOfferteItemsUpdated,
      onInDienstChanged,
      pakketten,
      rel04_08,
      indienst,
    },
    ref,
  ) => {
    const [, setLanguageVersion] = useState(0);
    useEffect(() => {
      const handler = () => setLanguageVersion((v) => v + 1);
      window.addEventListener("databasesReloaded", handler);
      return () => window.removeEventListener("databasesReloaded", handler);
    }, []);

    const t = getTranslations();
    useImperativeHandle(ref, () => ({
      // This function is triggered after we open a project in Navbar.
      // It sends loaded woning to buildingtree
      // Used to set expanded levels all open
      childFunction(loadedWoning: any, forced: boolean) {
        updateMainWoning(loadedWoning);
        setForeBuildingTreeUpdate(forced);
      },
    }));

    const [foreBuildingTreeUpdate, setForeBuildingTreeUpdate] = useState(false);
    const childRef = useRef<buildingTreeRefs>(null);
    const [visibleModal, setVisibleModal] = useState<string>("");

    const [selectedKast, setSelectedKast] = useState<Number>(0);
    const [selectedCtd, setSelectedCtd] = useState<Number>(1);

    useEffect(() => {
      if (foreBuildingTreeUpdate && loadedWoning) {
        if (childRef.current) {
          childRef.current.childFunction(woningTree, foreBuildingTreeUpdate);
        }
        setForeBuildingTreeUpdate(false); // Reset after call
        // setWoningDb(loadedWoning)
      }
    }, [foreBuildingTreeUpdate, loadedWoning]);

    // Static constants (sould remove these are they are the same as in functions)
    const woningIcon = (
      <Icon key="woning">
        <WoningIcon />
      </Icon>
    );

    const rootTreeItem: kamerItem = useMemo(
      () => ({
        id: "1",
        name: t.woning,
        key: "",
        nodeid: "1",
        children: [],
        expanded: true,
        icon: woningIcon,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [woningIcon, t.woning],
    );

    const initialSelectedNodeId = { id: "1", type: "woning", label: t.woning };

    // useState hooks for dynamic values
    const [woningDb, setWoningDb] = useState<dbItem[]>([]);
    const [woningTree, setWoningTree] = useState<kamerItem>(rootTreeItem);

    // Keep root node name in sync with language
    useEffect(() => {
      setWoningTree((prev) => {
        if (!prev || prev.nodeid !== "1") return prev;
        return { ...prev, name: t.woning };
      });
    }, [t.woning]);

    const [woningSelectedNodeId, setWoningSelectedNodeId] = useState(
      initialSelectedNodeId,
    );

    // useEffect for New / Open project
    useEffect(() => {
      if (!loadedWoning) {
        return;
      }
      // If loadedwoning is empty: try to restore from sessionstorage
      // This usually happens after a page refresh
      if (Object.keys(loadedWoning).length === 0) {
        const ls_after_refr = sessionStorage.getItem("loadedWoning");
        if (ls_after_refr) {
          loadedWoning = JSON.parse(ls_after_refr);
        }
      }

      // If still empty - just continue with empty
      // Usually happens after login
      // We should add a CTD and Kast here
      if (Object.keys(loadedWoning).length === 0) {
        // This adds an empty kast after login

        const kast2Add: dbItem = {
          label: t.kast + " 1",
          nodeid: "kast-0",
          parent: "0",
          type: "kast",
          typeIcon: "",
          outputOptions: {
            dmxType: "0",
            ctdId: 1,
            kastId: 0,
            moduletype: "",
          },
        };
        const kast2add2: dbItem[] = [];
        const ctd2Add: dbItem = {
          label: "CTD10",
          nodeid: "ctd-1",
          parent: "0",
          type: "controller",
          typeIcon: "",
          outputOptions: {
            dmxType: "0",
            ctdId: 1,
            kastId: 0,
            moduletype: "CTD10",
          },
        };
        kast2add2.push(kast2Add);
        kast2add2.push(ctd2Add);

        const updatedDb: dbItem[] = [...kast2add2];
        setSelectedKast(0);
        setSelectedCtd(1);
        updateMainWoning(updatedDb);
        // return;
      } else {
        // This gets triggered after page refresh and login
        // Not on New - is this why the kast is not added?
        //setSelectedKast("0")
        //setSelectedCtd("0")
        updateMainWoning(loadedWoning);
      }

      // If we have data (after refresh - or open file)
      // Build items for tree and save to db
    }, [loadedWoning]);

    // This function handles all update on db
    const updateMainWoning = (db: dbItem[]) => {
      // SAve to DB

      setWoningDb(db);
      onDbChange(db);
      sessionStorage.setItem("loadedWoning", JSON.stringify(db));

      // Build woningtree from db
      const woningtree: kamerItem = functions.buildWoningtreeFromDb(db);
      setWoningTree(woningtree);
    };

    // Called from buildingtree on removeItem
    const functionFromBuildingTreeRemoveItem = (nodes: kamerItem[]) => {
      // Check if the nodes include items of type woning.
      // If so we also delete the cildren in the item
      const findAllWoningItemIds = (nodes: kamerItem[]): string[] => {
        let result: string[] = [];

        for (const item of nodes) {
          const icon = item.icon;

          if (React.isValidElement(icon) && String(icon.key) === "woning") {
            result.push(item.id);
          }

          if (Array.isArray(item.children)) {
            result = result.concat(findAllWoningItemIds(item.children));
          }
        }

        return result;
      };

      const woningIds = findAllWoningItemIds(nodes);

      // Make a list of ids we want to delete
      const idsToRemove = new Set([
        ...nodes.map((item) => item.id), // originally selected nodes
        ...woningIds, // any node with icon.key === 'woning'
      ]);
      // loop through woningDb and remove items in idsToRemove
      // And items who have a parent in woningIds
      const updatedDb = woningDb.filter(
        (item: dbItem) =>
          !idsToRemove.has(item.nodeid) && !idsToRemove.has(item.parent),
      );
      // Update the Db and tree
      updateMainWoning(updatedDb);
    };

    // Called from buildingtree on moveNod
    const functionFromBuildingTreeMoveItem = (
      itemToMove: kamerItem[],
      newParent: kamerItem,
    ) => {
      // Make a list of ids we want to move
      const idsToMove = new Set(itemToMove.map((item) => item.id));
      // loop through woningDb and change parent of items that we want to move
      const updatedDb = woningDb.map((item: dbItem) =>
        idsToMove.has(item.nodeid) ? { ...item, parent: newParent.id } : item,
      );
      // Update the Db and tree
      updateMainWoning(updatedDb);
    };

    // Called from buildingtree on changeItemName
    const functionFromBuildingTreeChangeItemName = (
      itemId: string,
      newName: string,
    ) => {
      // Change te name (label) of the item
      const updatedItems = woningDb.map((item: dbItem) =>
        item.nodeid === itemId
          ? { ...item, label: newName } // ✅ Update only this one
          : item,
      );
      // Update the Db and tree
      updateMainWoning(updatedItems);
    };

    function duplicateNodeWithChildren(
      sourceNodeId: string,
      newName: string,
    ): dbItem[] {
      // Find the node to duplicate
      debugger;
      const sourceNode = woningDb.find((n) => n.nodeid === sourceNodeId);
      if (!sourceNode) return woningDb;

      // Generate a unique ID (higher than any existing one)
      /*
      const maxId = woningDb.reduce((max, n) => {
        const id = parseInt(n.nodeid);
        return isNaN(id) ? max : Math.max(max, id);
      }, 0);
*/
      let maxId = Number(sessionStorage.getItem("nodeId")?.toString()) || 1;
      let nextId = maxId + 1;

      // Create the new parent node
      const newParentId = String(nextId++);
      const newParentNode: dbItem = {
        ...sourceNode,
        nodeid: newParentId,
        label: newName,
      };

      // Find all direct children and remap their parentid to the new parent
      const children = woningDb.filter((n) => n.parent === sourceNodeId);
      const newChildren: dbItem[] = children.map((child) => ({
        ...child,
        nodeid: String(nextId++),
        parent: newParentId,
      }));
sessionStorage.setItem("nodeId", nextId.toString());
      return [...woningDb, newParentNode, ...newChildren];
    }

    // Called from buildingtree on duplicateRoom
    const functionFromBuildingTreeDuplicateRoom = (
      itemId: string,
      newName: string,
    ) => {
      const newNode = duplicateNodeWithChildren(itemId, newName);
      updateMainWoning(newNode);
    };

    // Called from buildingtree on changeItemIcon
    const functionFromBuildingTreeChangeIcon = (
      itemId: string,
      icon: string,
    ) => {
      // loop through woningDb and change parent of items that we want to move
      const updatedItems = woningDb.map((item: dbItem) =>
        item.nodeid === itemId
          ? { ...item, typeIcon: icon } // ✅ Update only this one
          : item,
      );
      // Update the Db and tree
      updateMainWoning(updatedItems);
    };

    //Called from from outputmodules - New item added -> update woningDb
    const HandleModulesItemClicked = (moduleItem: dbItem[]) => {
      // Add all items to db
      const updatedDb: dbItem[] = [...woningDb, ...moduleItem];
      updateMainWoning(updatedDb);
    };

    const getNextkastId = (items: dbItem[]) => {
      const kastIds = items
        .filter((item) => item.type === "kast")
        .map((item) => item.outputOptions.kastId);

      return {
        nextkastId: kastIds.length > 0 ? Math.max(...kastIds) + 1 : 0,
      };
    };

    const getNextCtdId = (items: dbItem[]) => {
      const ctdIds = items
        .filter((item) => item.type === "controller")
        .map((item) => item.outputOptions.ctdId);

      return {
        nextCtdId: ctdIds.length > 0 ? Math.max(...ctdIds) + 1 : 1,
      };
    };

    // If we click on a room -> Set ID (so we know in wich room to put the outputs)
    const HandleWoningItemClicked = (
      type: string,
      nodeId: string,
      label: string,
    ) => {
      const updatedSelectedNode = functions.ChangeSelectedFloor(
        woningSelectedNodeId,
        nodeId,
        type,
        label,
      );
      setWoningSelectedNodeId(updatedSelectedNode);
    };

    // Store offerte items in case we want to do an export
    const HandleOfferte = (
      modules: modsForOfferteArray[],
      noSaMods: modsForOfferteArray[],
    ) => {
      onOfferteItemsUpdated(modules, noSaMods);
      sessionStorage.setItem("offerteModules", JSON.stringify(modules));
    };

    // From Full Offerte - Set Indiens prijs
    const handleInDienstChanged = (ind: boolean) => {
      onInDienstChanged(ind);
    };

    //Called from from kast opbouw - Add new kast to woningDb
    const HandleAddKastFromKastview = () => {
      setVisibleModal("addkast");
    };

    // Close add kast component
    const handleCloseAddKast = (eventKey: string | null) => {
      setVisibleModal("");
    };

    // Save kast
    const handleSaveKast = (kastnaam: string) => {
      const kastId = getNextkastId(woningDb);

      const kast2Add: dbItem = {
        label: kastnaam,
        nodeid: "kast-" + String(kastId.nextkastId),
        parent: "0",
        type: "kast",
        typeIcon: "",
        outputOptions: {
          dmxType: "0",
          ctdId: 0,
          kastId: kastId.nextkastId,
          moduletype: "",
        },
      };
      const kast2add2: dbItem[] = [];
      kast2add2.push(kast2Add);

      // Add all items to db
      const updatedDb: dbItem[] = [...woningDb, ...kast2add2];
      updateMainWoning(updatedDb);
      setSelectedKast(kastId.nextkastId);
    };

    //Called from from kast opbouw - Add new ctd to woningDb
    const HandleAddCtdFromKastview = () => {
      setVisibleModal("addctd");
    };

    // Close add ctd component
    const handleCloseAddCtd = (eventKey: string | null) => {
      setVisibleModal("");
    };

    // Save ctd
    const handleSaveCtd = (ctdnaam: string) => {
      const ctdId = getNextCtdId(woningDb);

      //console.log(ctdId.nextCtdId)

      const ctd2Add: dbItem = {
        label: ctdnaam,
        nodeid: "ctd-" + String(ctdId.nextCtdId),
        parent: "0",
        type: "controller",
        typeIcon: "",
        outputOptions: {
          dmxType: "0",
          ctdId: ctdId.nextCtdId,
          kastId: Number(selectedKast),
          moduletype: "CTD10",
        },
      };
      const ctd2add2: dbItem[] = [];
      ctd2add2.push(ctd2Add);

      // Add all items to db
      const updatedDb: dbItem[] = [...woningDb, ...ctd2add2];

      updateMainWoning(updatedDb);
      setSelectedCtd(ctdId.nextCtdId);
    };

    const handleSetCtd = (id: string) => {
      setSelectedCtd(Number(id));
      console.log("Set Ctd to: ", id);
    };

    const handleSetKast = (id: string) => {
      setSelectedKast(Number(id));
      console.log("Set kast to: ", id);
    };

    return (
      <>
        <Container>
          <br />
          <Row>
            <Col xs={3}>
              <BuildingTree
                ref={childRef}
                treeviewArrayFromOpbouw={woningTree}
                onWoningItemClick={HandleWoningItemClicked}
                onNameChange={functionFromBuildingTreeChangeItemName}
                onDuplicate={functionFromBuildingTreeDuplicateRoom}
                onIconChange={functionFromBuildingTreeChangeIcon}
                onRemoveItem={functionFromBuildingTreeRemoveItem}
                onMoveItem={functionFromBuildingTreeMoveItem}
              />
            </Col>

            <Col xs={4}>
              <OutputModulesTree
                woningSlectedId={woningSelectedNodeId}
                addToParentDb={HandleModulesItemClicked}
                uitvoering={uitvoering}
                ctdId={selectedCtd}
                kastId={selectedKast}
              />
            </Col>

            <Col xs={5}>
              <Overzicht
                db={woningDb}
                onOfferteChange={HandleOfferte}
                indienst={indienst}
                onInDienstChanged={handleInDienstChanged}
                addKastToParentDb={HandleAddKastFromKastview}
                addCtdToParentDb={HandleAddCtdFromKastview}
                setCtdToParent={handleSetCtd}
                setKastToParent={handleSetKast}
                ctdId={selectedCtd}
                kastId={selectedKast}
              />
            </Col>
          </Row>
        </Container>

        <AddKast
          visible={visibleModal}
          onDataChange={handleCloseAddKast}
          onSave={handleSaveKast}
        />

        <AddCtd
          visible={visibleModal}
          onDataChange={handleCloseAddCtd}
          onSave={handleSaveCtd}
        />
      </>
    );
  },
);

export default Opbouw;
